"use client";

import Link from "next/link";
import { MessageSellerButton } from "@/components/messages/MessageSellerButton";
import { ResponseTimeBadge } from "@/components/messages/ChatBits";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SocialLinks } from "@/components/ui/social-links";
import { SellerTrustScore } from "@/components/ui/seller-trust-score";
import { UserBadges } from "@/components/ui/user-badges";
import { SubscriptionBadge } from "@/components/ui/subscription-badge";
import { ProfileAdminControls } from "@/components/admin/ProfileAdminControls";
import Globe from "lucide-react/dist/esm/icons/globe";
import Calendar from "lucide-react/dist/esm/icons/calendar";
import MapPin from "lucide-react/dist/esm/icons/map-pin";
import Clock from "lucide-react/dist/esm/icons/clock";
import Briefcase from "lucide-react/dist/esm/icons/briefcase";
import Share2 from "lucide-react/dist/esm/icons/share-2";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import { formatDistanceToNow } from "date-fns";
import { getCountryByCode } from "@/lib/countries";
import { toast as sonnerToast } from "sonner";
import type { PendingIntent } from "@/lib/pending-intent";
import { isCommissionBanned } from "@/lib/commission-standing";

interface SellerProfileHeaderProps {
  seller: any;
  sellerTier: any;
  roles: any;
  trustScore: any;
  isFollowing: boolean;
  isOwnProfile: boolean;
  isFollowPending: boolean;
  isUnfollowPending: boolean;
  currentUserProfile: any;
  username: string;
  onHireMeClick: () => void;
  onFollow: () => void;
  onUnfollow: () => void;
  onAuthRequired: (message: string, onSuccess: () => void, intent?: PendingIntent) => void;
}

export function SellerProfileHeader({
  seller,
  sellerTier,
  roles,
  trustScore,
  isFollowing,
  isOwnProfile,
  isFollowPending,
  isUnfollowPending,
  currentUserProfile,
  username,
  onHireMeClick,
  onFollow,
  onUnfollow,
  onAuthRequired,
}: SellerProfileHeaderProps) {
  // Hidden, not disabled: a blocked account shouldn't be shown a door it can't open.
  const viewerCommissionBanned = isCommissionBanned(currentUserProfile);

  const handleShareProfile = () => {
    const shareUrl = `https://uefnmarketplace.com/store/${seller.username || username}`;
    navigator.clipboard.writeText(shareUrl).then(() => {
      sonnerToast.success("Share link copied!");
    });
  };

  const handleHireMeClick = () => {
    if (isOwnProfile) return;
    if (!currentUserProfile) {
      onAuthRequired("Hire this creator", onHireMeClick);
      return;
    }
    onHireMeClick();
  };

  return (
    <aside className="lg:w-80 shrink-0">
      <div className="lg:sticky lg:top-24 space-y-4">
        <Card className="bg-card border border-border overflow-hidden">
          <CardContent className="p-6">
            {/* Avatar + Name */}
            <div className="text-center">
              <Avatar className="h-28 w-28 mx-auto border-4 border-background shadow-md">
                <AvatarImage src={seller.avatar_url || undefined} />
                <AvatarFallback className="text-3xl font-display bg-primary text-primary-foreground">
                  {seller.display_name?.charAt(0) || "S"}
                </AvatarFallback>
              </Avatar>

              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <h1 className="text-xl font-display font-bold">
                  {seller.display_name || seller.username}
                </h1>
                {sellerTier && sellerTier !== "free" && (
                  <SubscriptionBadge tier={sellerTier} size="md" />
                )}
                <UserBadges
                  user={{
                    is_seller: true,
                    seller_verified: seller.seller_verified,
                    is_early_founder: seller.is_early_founder,
                    roles: roles || [],
                  }}
                  size="sm"
                />
              </div>

              {seller.username && (
                <p className="text-sm text-muted-foreground font-mono">@{seller.username}</p>
              )}
            </div>

            {seller.bio && (
              <p className="mt-4 text-sm text-muted-foreground text-center leading-relaxed">
                {seller.bio}
              </p>
            )}

            {/* Action Buttons */}
            <div className="mt-5 space-y-2">
              {seller.hireable && !viewerCommissionBanned && (
                <Button
                  className="w-full font-semibold"
                  onClick={handleHireMeClick}
                  disabled={isOwnProfile}
                >
                  <Briefcase className="h-4 w-4 mr-2" />
                  Hire Me
                  {(seller.hire_min_budget ?? seller.hire_starting_price) && (
                    <span className="ml-1 opacity-80">from ${seller.hire_min_budget ?? seller.hire_starting_price}</span>
                  )}
                </Button>
              )}

              {!isOwnProfile && (
                <MessageSellerButton
                  recipientId={seller.id}
                  recipientName={seller.display_name || seller.username || "this creator"}
                  contextType="store"
                  label="Message"
                  variant="outline"
                  className="w-full"
                />
              )}
              {!isOwnProfile && <ResponseTimeBadge profileId={seller.id} className="w-full justify-center" />}

              {!isOwnProfile && (
                <Button
                  className="w-full"
                  variant={isFollowing ? "outline" : "default"}
                  onClick={() => {
                    if (!currentUserProfile) {
                      onAuthRequired("Follow this creator", onFollow, {
                        kind: "follow",
                        sellerProfileId: seller.id,
                        sellerName: seller.display_name || seller.username || undefined,
                      });
                      return;
                    }
                    (isFollowing ? onUnfollow : onFollow)();
                  }}
                  disabled={isFollowPending || isUnfollowPending}
                >
                  {isFollowing ? "Unfollow" : "Follow"}
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground hover:text-foreground"
                onClick={handleShareProfile}
              >
                <Share2 className="mr-2 h-4 w-4" />
                Share Profile
              </Button>

              {/* Admin-only: manage this user in place (renders nothing for non-admins). */}
              <ProfileAdminControls profileId={seller.id} />
            </div>

            {/* Hire Specialties */}
            {seller.hireable && seller.hire_specialties?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border/50">
                <p className="font-mono text-2xs uppercase tracking-widest text-primary mb-3">
                  Specialties
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {seller.hire_specialties.map((tag: string, i: number) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-2xs bg-primary/10 border-primary/20 text-primary"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                {seller.hire_description && (
                  <p className="mt-2 text-xs text-muted-foreground">{seller.hire_description}</p>
                )}
              </div>
            )}

            {/* Meta Info */}
            <div className="mt-5 pt-5 border-t border-border/50 space-y-2.5 text-sm">
              {seller.show_country_on_profile && seller.country_code && (
                <div className="flex items-center gap-2.5 text-muted-foreground">
                  <MapPin className="h-4 w-4 shrink-0" />
                  <span>
                    {getCountryByCode(seller.country_code)?.flag}{" "}
                    {getCountryByCode(seller.country_code)?.name}
                  </span>
                </div>
              )}
              {seller.website && (
                <div className="flex items-center gap-2.5 text-muted-foreground">
                  <Globe className="h-4 w-4 shrink-0" />
                  <a
                    href={seller.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 truncate transition-colors"
                  >
                    {seller.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
              <div className="flex items-center gap-2.5 text-muted-foreground">
                <Calendar className="h-4 w-4 shrink-0" />
                <span>
                  Joined{" "}
                  {new Date(seller.created_at).toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
              {seller.last_active_at && (
                <div className="flex items-center gap-2.5 text-muted-foreground">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span>
                    Active{" "}
                    {formatDistanceToNow(new Date(seller.last_active_at), { addSuffix: true })}
                  </span>
                </div>
              )}

              <SocialLinks
                links={{
                  social_youtube: seller.social_youtube,
                  social_twitter: seller.social_twitter,
                  social_discord: seller.social_discord,
                  social_tiktok: seller.social_tiktok,
                  social_instagram: seller.social_instagram,
                }}
                className="pt-2"
              />
            </div>

            {/* Trust Score */}
            {trustScore && (
              <div className="mt-5 pt-5 border-t border-border/50 flex justify-center">
                <SellerTrustScore result={trustScore} size="lg" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </aside>
  );
}
